import { captureException, captureMessage } from '@sentry/react';
import { useEffect } from 'react';
import { match, not, __ } from 'ts-pattern';
import { isElectron, isExternalSession, noop } from '../utils';
import usePreferences from './usePreferences';

const matchMedia = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');

const getElectronPreferedTheme = () =>
  new Promise(resolve => {
    const { ipcRenderer } = window.require('electron');
    ipcRenderer.send('get-native-theme-mode');

    // in case receiving event takes too long, finishing promise with default theme mode
    const timeoutId = setTimeout(() => {
      captureMessage('Theme mode fetching time exceeded');
      resolve('light');
    }, 5000);

    ipcRenderer.on('get-native-theme-mode', (event, arg) => {
      ipcRenderer.removeAllListeners('get-native-theme-mode');
      clearTimeout(timeoutId);
      resolve(arg.themeMode);
    });
  });
const getPreferredColorScheme = async () => {
  if (isElectron()) {
    const theme = await getElectronPreferedTheme();

    return theme;
  }
  return matchMedia && matchMedia.matches ? 'dark' : 'light';
};

const addColorSchemeChangeListener = callback => {
  if (isElectron()) {
    const { ipcRenderer } = window.require('electron');
    ipcRenderer.send('native-theme-mode-change');
    ipcRenderer.on('native-theme-mode-change', (event, arg) => {
      callback({ matches: arg.themeMode === 'dark' });
    });
  } else if (matchMedia) {
    matchMedia.addEventListener('change', callback);
  }
};
const removeColorSchemeChangeListener = callback => {
  if (isElectron()) {
    const { ipcRenderer } = window.require('electron');
    ipcRenderer.removeAllListeners('native-theme-mode-change');
  } else if (matchMedia) {
    matchMedia.removeEventListener('change', callback);
  }
};
const getDefaultTheme = () => {
  // In external Inbox sessions  light theme should be used regardless of preference
  if (isExternalSession()) {
    return 'light';
  }
  return localStorage.getItem('theme') || 'light';
};

const useThemeMode = () => {
  const {
    preferences: { activeTheme: theme = getDefaultTheme(), autoDetectTheme },
    updatePreferences
  } = usePreferences();
  const setMode = ({
    mode,
    isAuto = autoDetectTheme,
    actionProps,
    onSuccess,
    onError,
    ignoreElectron = false
  }) => {
    const validated = ['light', 'dark'].includes(mode) ? mode : 'light';
    if (isElectron() && !ignoreElectron) {
      const { ipcRenderer } = window.require('electron');
      ipcRenderer.send('set-native-theme-mode', { themeMode: isAuto ? 'system' : validated });
    }
    updatePreferences(
      { activeTheme: validated, autoDetectTheme: isAuto },
      actionProps,
      onSuccess,
      onError
    );
  };

  const toggleTheme = async (custom, actionProps, onSuccess, onError) => {
    try {
      const getNewTheme = match([custom, theme])
        .with(['auto', __], () => getPreferredColorScheme)
        .with(
          [not(__.nullish), __],
          ([customTheme]) =>
            () =>
              Promise.resolve(customTheme)
        )
        .with([__, 'light'], () => () => Promise.resolve('dark'))
        .otherwise(() => () => Promise.resolve('light'));
      const newTheme = await getNewTheme();
      const auto = custom === 'auto';
      setMode({ mode: newTheme, isAuto: auto, actionProps, onSuccess, onError });
    } catch (err) {
      captureException(err);
    }
  };

  // saving theme to localstorage in order to apply theme before authentication is complete.
  // it can be overwritten if firebase stored theme is different
  useEffect(() => {
    // External sessions should not effect localStorage as it's shared
    if (!isExternalSession()) {
      localStorage.setItem('theme', theme || getDefaultTheme());
    }
  }, [theme]);

  useEffect(() => {
    if (!autoDetectTheme) {
      return noop;
    }
    const handleChangeMode = event => {
      const newColorScheme = event.matches ? 'dark' : 'light';
      // ignoreElectron is supposed to ignore updating native theme, which causes change event loop
      setMode({ mode: newColorScheme, ignoreElectron: true });
    };
    addColorSchemeChangeListener(handleChangeMode);
    return () => {
      removeColorSchemeChangeListener(handleChangeMode);
    };
  }, [autoDetectTheme]);

  return [theme, toggleTheme];
};
export default useThemeMode;
